import type { ReactNode } from 'react'
import type { HeadingSegment } from '@/components/generals/animated-heading'

import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Reveal } from '@/components/generals/reveal'
import { AnimatedHeading } from '@/components/generals/animated-heading'

type SectionHeadingProps = {
    eyebrow?: string
    segments: HeadingSegment[]
    subtitle?: string
    as?: 'h1' | 'h2'
    align?: 'center' | 'left'
    className?: string
    headingClassName?: string
    children?: ReactNode
}

export const HEADING_XL = 'text-[clamp(2.5rem,6.2vw,5rem)] leading-[1.1] tracking-[-0.04em] font-medium text-white'
export const HEADING_LG = 'text-[clamp(2rem,4.6vw,3.75rem)] leading-[1.1] tracking-[-0.04em] font-medium text-white'
export const HEADING_MD = 'text-[clamp(1.75rem,3.2vw,2.625rem)] leading-[1.15] tracking-[-0.04em] font-medium text-white'

export const SectionHeading = ({
    eyebrow,
    segments,
    subtitle,
    as = 'h2',
    align = 'center',
    className,
    headingClassName,
    children
}: SectionHeadingProps) => {
    const centered = align === 'center'

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-5',
                centered ? 'items-center text-center' : 'items-start text-left',
                className
            )}
        >
            {eyebrow ? (
                <Reveal
                    y={12}
                    blur={6}
                    duration={0.7}
                >
                    <Eyebrow>
                        {eyebrow}
                    </Eyebrow>
                </Reveal>
            ) : null}

            <AnimatedHeading
                as={as}
                segments={segments}
                className={cn(
                    as === 'h1' ? HEADING_XL : HEADING_LG,
                    centered && 'max-w-[18ch] mx-auto',
                    headingClassName
                )}
            />

            {subtitle ? (
                <Reveal
                    y={16}
                    delay={0.1}
                    duration={0.8}
                    className={cn(
                        'text-[clamp(1rem,1.2vw,1.125rem)] leading-[1.55] tracking-[-0.01em] text-white/56',
                        centered ? 'max-w-[56ch]' : 'max-w-[54ch]'
                    )}
                >
                    {subtitle}
                </Reveal>
            ) : null}

            {children}
        </div>
    )
}
